import { Badge } from "./badge";
import { Dialog, DialogContent } from "./dialog";
import { Spinner } from "./spinner";
import { Code, Text } from "../primitives/typography";
import { VideoPlayer, type VideoPlayerCaption } from "./VideoPlayer";
import { formatDateTime } from "../../../../apps/global/formatHelpers";

/** Everything the player needs, already resolved. Whoever fetches it owns the shape it came in. */
export interface VideoPreviewPlayback {
    manifestUrl: string;
    posterUrl?: string | null;
    captions: VideoPlayerCaption[];
}

export interface VideoPreviewDialogProps {
    open: boolean;
    onClose: () => void;
    /**
     * Null means "nothing to play" — which is an ordinary answer, not a failure, and is rendered
     * as such once `loading` is done and no `errorMessage` came back.
     */
    playback?: VideoPreviewPlayback | null;
    loading?: boolean;
    /** A message to show in place of the player. Already user-facing by the time it arrives here. */
    errorMessage?: string | null;
    title: string;
    organizationName?: string;
    description?: string | null;
    tags?: { name: string }[];
    /**
     * When the upload was recorded — an ISO string or epoch ms. Named for the row's `createdAt`
     * rather than "uploaded", because the row exists from the moment `createVideoUpload` runs,
     * before the bytes have finished moving or transcoding.
     */
    createdAt?: string | number | null;
}

/**
 * Previews a video.
 *
 * Presentational: it takes resolved playback and renders the four states it can be in. Fetching
 * stays with the caller, because how you address a video differs by surface — the Library asks by
 * asset, since an upload need not be attached to any content, while a learner-facing player asks
 * by content and runs an eligibility layer the Library has no use for.
 *
 * Every outcome says something. The play control on a card used to be a decorative `<div>`, so a
 * video that could not play and a video with nothing wired to it looked identical: nothing
 * happened. A preview that cannot start now explains why.
 */
export const VideoPreviewDialog = ({
    open,
    onClose,
    playback,
    loading,
    errorMessage,
    title,
    organizationName,
    description,
    tags,
    createdAt,
}: VideoPreviewDialogProps) => {
    return (
        <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
            {/*
             * `pt-12`: the dialog's close button sits 16px from the top, and with the default padding
             * the video started right under it — the × was drawn on the video's edge.
             */}
            <DialogContent className="flex max-h-[90vh] max-w-3xl flex-col overflow-hidden pt-12">
                <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto">
                    {loading && (
                        <div className="flex h-64 items-center justify-center">
                            <Spinner className="size-6" />
                        </div>
                    )}

                    {!loading && errorMessage && (
                        <Text role="alert" className="text-destructive text-sm">
                            {errorMessage}
                        </Text>
                    )}

                    {/*
                     * Null is the ordinary "not playable" answer, not a failure: a pre-cutover row has
                     * no bytes at all, and dionys withholds a URL until transcode finishes. Both look
                     * the same from here, and both are worth saying out loud rather than showing an
                     * empty box.
                     */}
                    {!loading && !errorMessage && !playback && (
                        <Text muted className="text-sm">
                            This video isn&apos;t ready to play yet. If it was just uploaded, transcoding may still be
                            running.
                        </Text>
                    )}

                    {playback && (
                        <div className="relative shrink-0 overflow-hidden rounded-lg" style={{ aspectRatio: "16/9" }}>
                            <VideoPlayer
                                src={playback.manifestUrl}
                                poster={playback.posterUrl}
                                captions={playback.captions}
                            />
                        </div>
                    )}
                    <div className="bg-accent rounded-xl p-4">
                        <Text className="font-bold">{title}</Text>

                        {organizationName && (
                            <Text muted className="text-sm">
                                {organizationName}
                            </Text>
                        )}

                        {description && (
                            <Text muted className="mt-4 text-sm">
                                {description}
                            </Text>
                        )}
                        {createdAt && <Code className="mt-4 text-xs">Added {formatDateTime(createdAt)}</Code>}
                    </div>

                    {tags && tags.length > 0 && (
                        <div className="flex flex-wrap items-center gap-1">
                            {tags.map((tag) => (
                                <Badge key={tag.name} variant="tag" className="mt-1">
                                    {tag.name}
                                </Badge>
                            ))}
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};
